import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import PrintableStageInspection from '@/components/quality/PrintableStageInspection';
import {
  useInspectionAttachments,
  useInspectionCheckpoints,
  useStageInspection,
  useStageInspectionMeasurements,
} from '@/hooks/useQCWorkflow';
import { useSystemSettings } from '@/hooks/useSettings';

const DimensionalInspectionPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const didTriggerPrint = useRef(false);
  const printRootRef = useRef<HTMLDivElement | null>(null);
  const { data: inspection, isLoading, isError, error } = useStageInspection(id || '', !!id);
  const { data: measurements = [], isLoading: loadingMeasurements } = useStageInspectionMeasurements(id || '');
  const { data: checkpoints = [], isLoading: loadingCheckpoints } = useInspectionCheckpoints(id || '');
  const { data: attachments = [], isLoading: loadingAttachments } = useInspectionAttachments(id || '');
  const { data: settings = [], isLoading: loadingSettings } = useSystemSettings();

  const companyInfo = useMemo(() => {
    const companySettings = settings.filter((setting) => setting.category === 'company');
    const getSetting = (key: string) =>
      companySettings.find((setting) => setting.setting_key === key)?.setting_value?.trim() ?? '';

    return {
      name: getSetting('company_name') || 'Company',
      address:
        [
          getSetting('company_address'),
          getSetting('company_address_line1'),
          getSetting('company_city'),
          getSetting('company_emirate_code'),
          getSetting('company_country_code'),
        ]
          .filter(Boolean)
          .join(', ') || 'Company Address',
      phone: getSetting('company_phone') || getSetting('company_mobile') || '',
      email: getSetting('company_email') || '',
      taxNumber: getSetting('company_trn') || '',
      logo: getSetting('company_logo') || undefined,
    };
  }, [settings]);

  const back = () => {
    navigate('/qms/dimensional-inspection', { replace: true });
  };

  useEffect(() => {
    const styleId = 'dimensional-inspection-print-page-size';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = `
        @media print {
          @page {
            size: A4 landscape !important;
            margin: 0 !important;
          }

          html, body {
            width: 297mm !important;
            min-width: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
        }
      `;
      document.head.appendChild(styleEl);
    }

    return () => {
      styleEl?.remove();
    };
  }, []);

  useEffect(() => {
    if (
      !inspection ||
      isLoading ||
      loadingMeasurements ||
      loadingCheckpoints ||
      loadingAttachments ||
      loadingSettings ||
      didTriggerPrint.current
    ) {
      return;
    }

    didTriggerPrint.current = true;
    let cancelled = false;

    const handleAfterPrint = () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      if (window.opener && !window.opener.closed) {
        window.close();
        window.setTimeout(() => {
          if (!window.closed) back();
        }, 300);
        return;
      }
      back();
    };

    const waitForImages = async () => {
      const root = printRootRef.current;
      if (!root) return;
      const images = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
      await Promise.all(
        images.map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            })
        )
      );
    };

    const runPrint = async () => {
      try {
        if (document.fonts?.ready) {
          await document.fonts.ready;
        }
        await waitForImages();
        await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
        if (cancelled) return;
        window.print();
      } catch {
        if (!cancelled) window.print();
      }
    };

    window.addEventListener('afterprint', handleAfterPrint);
    const timeoutId = window.setTimeout(() => {
      void runPrint();
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [
    back,
    inspection,
    isLoading,
    loadingAttachments,
    loadingCheckpoints,
    loadingMeasurements,
    loadingSettings,
  ]);

  if (!id) {
    return <Navigate to="/qms/dimensional-inspection" replace />;
  }

  if (isLoading || loadingMeasurements || loadingCheckpoints || loadingAttachments) {
    return (
      <PrintPreviewChrome title="Dimensional inspection - print preview" onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !inspection) {
    return (
      <PrintPreviewChrome title="Dimensional inspection - print preview" onBack={back}>
        <div style={{ padding: 48 }}>
          <Alert
            type="error"
            showIcon
            message="Could not load dimensional inspection"
            description={(error as Error)?.message || 'Check the record and your permissions.'}
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  return (
    <PrintPreviewChrome title="Dimensional inspection - print preview" onBack={back}>
      <div ref={printRootRef} className="print-doc-a4 print-doc-a4--dimensional-landscape">
        <PrintableStageInspection
          sci={inspection}
          workOrderNumber={inspection.workOrder?.work_order_number || inspection.work_order_id}
          checkpoints={checkpoints}
          measurements={measurements}
          attachments={attachments}
          companyInfo={companyInfo}
        />
      </div>
    </PrintPreviewChrome>
  );
};

export default DimensionalInspectionPrintPage;
